/**
 * Centralized analytics module for V3 quiz funnel.
 * Purely functional — no React hooks. Safe to call from any context.
 *
 * Fires events to:
 *  - Amplitude (@amplitude/analytics-browser) — funnel stage events
 *  - GTM / GA4 (window.dataLayer) — funnel stage events
 *  - Meta — via dataLayer only (NO direct fbq() calls):
 *    App code → pushMetaEvent() → dataLayer → GTM Pixel_Event tag → fbq() [browser]
 *                                            → GTM GA4 tag → sGTM → Stape CAPI tag [server]
 *  - AppLovin Axon — via dataLayer only (NO direct axon() calls from milestones):
 *    App code → pushAxonEvent() → dataLayer → GTM AppLovin tags → axon("track", …) [browser]
 *    Axon init is deferred via marketingTags when REACT_APP_AXON_EVENT_KEY is set.
 *
 * Only 4 standard events reach Meta: Lead, ViewContent, InitiateCheckout, Purchase.
 * All other events (funnel_started, quiz_completed, etc.) go to GA4/Amplitude only.
 * PII fields (em, fn, ln) are sent raw — sGTM/Stape hashes them server-side.
 */
import * as amplitude from '@amplitude/analytics-browser';
import { getStoredUTMs } from '../../../utils/utm';
import { ALL_EXPERIMENT_KEYS } from '../../../config/experiments';
import { getAbDefaultPlanVariant } from '../../../lib/abDefaultPlan';
import { getFunnelSource } from '../../../utils/loops';
import { LTV_MODEL } from '../../../utils/predictLTV';
import {
    ensureDataLayer,
    ensureMarketingTagsReady,
} from '../../../utils/marketingTags';

const AXON_LEAD_VALUE = Number(process.env.REACT_APP_AXON_LEAD_VALUE || '10');
const AXON_DEFAULT_ITEM = {
    item_id: 'move_together_walking_plan',
    item_name: 'Move Together Walking Plan',
    item_category_id: 499,
    quantity: 1,
} as const;

function buildAxonItem(
    planType: string,
    price: number,
    extra?: Record<string, unknown>
) {
    return {
        ...AXON_DEFAULT_ITEM,
        item_id: planType || AXON_DEFAULT_ITEM.item_id,
        item_name: planType ? `Move Together ${planType} plan` : AXON_DEFAULT_ITEM.item_name,
        price,
        ...extra,
    };
}

interface AxonUserData {
    email?: string;
    user_id?: string;
}

function buildAxonUserData(overrides?: { em?: string }): AxonUserData {
    const email = overrides?.em || _userEmail;
    return {
        ...(email && { email: email.toLowerCase().trim() }),
        user_id: getOrCreateExternalId(),
    };
}

/**
 * Push an AppLovin-specific event to the dataLayer.
 * GTM AppLovin tags listen on axon_* event names only — Meta tags are unchanged.
 */
function isAxonEnabled(): boolean {
    return Boolean(process.env.REACT_APP_AXON_EVENT_KEY?.trim());
}

function pushAxonEvent(axonEventName: string, eventData?: Record<string, any>) {
    if (!isAxonEnabled() || typeof window === 'undefined') return;

    ensureDataLayer();
    window.dataLayer.push({
        event: `axon_${axonEventName}`,
        axon_event_name: axonEventName,
        axon_event_data: eventData ?? {},
    });
}

function axonFireOnce(eventKey: string): boolean {
    if (typeof sessionStorage === 'undefined') return true;
    const key = `axon_fired_${eventKey}`;
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    return true;
}

// ── Event names — one per funnel stage (Amplitude + GTM/GA4) ────────────────
//
// Standard names that match both GA4 recommended events and Meta standard events.
// Only funnel milestones get tracked — no micro-events.

export const FUNNEL_V3_EVENTS = {
    FUNNEL_STARTED: 'funnel_started',
    QUIZ_COMPLETED: 'quiz_completed',
    LEAD: 'lead',
    RESULTS_VIEWED: 'results_viewed',
    PAYWALL_VIEWED: 'paywall_viewed',
    PAYWALL_VIEW: 'paywall_view',
    SELECT_PLAN: 'select_plan',
    BEGIN_CHECKOUT: 'begin_checkout',
    ADD_PAYMENT_INFO: 'add_payment_info',
    PURCHASE: 'purchase',
    ACCOUNT_CREATED: 'account_created',
} as const;

// ── Module-level user state ─────────────────────────────────────────────────
// Restored from persisted store on module load so email is available for
// Meta user_data even after a page reload mid-funnel.
let _userEmail = '';
let _userFirstName = '';
let _userLastName = '';
try {
    if (typeof localStorage !== 'undefined') {
        const stored = JSON.parse(localStorage.getItem('funnel-v3-storage') || '{}');
        _userEmail = stored?.state?.data?.email || '';
        // Also restore from mc_user_* localStorage (set after purchase)
        _userFirstName = localStorage.getItem('mc_user_fn') || '';
        _userLastName = localStorage.getItem('mc_user_ln') || '';
    }
} catch { /* ignore parse errors */ }

// ── Meta event types ─────────────────────────────────────────────────────────

interface MetaEventPayload {
    meta_event_name: 'Lead' | 'ViewContent' | 'InitiateCheckout' | 'Purchase';
    event_id: string;
    value?: number;
    currency?: string;
    content_name?: string;
    order_id?: string;
    predicted_ltv?: number;
    actual_amount?: number;
    user_data: {
        em?: string;
        fn?: string;
        ln?: string;
        external_id?: string;
        fbp?: string;
        fbc?: string;
        client_user_agent?: string;
    };
    // GA4 properties passed through on the same dataLayer push
    [key: string]: any;
}

// ── Cookie / fbclid utilities ────────────────────────────────────────────────

function getFbpCookie(): string | undefined {
    if (typeof document === 'undefined') return undefined;
    const match = document.cookie.match(/_fbp=([^;]+)/);
    return match ? match[1] : undefined;
}

function getFbcValue(): string | undefined {
    if (typeof document === 'undefined') return undefined;
    const cookieMatch = document.cookie.match(/_fbc=([^;]+)/);
    if (cookieMatch) return cookieMatch[1];

    // First-touch fbc persisted before Meta pixel sets _fbc (or when pixel is blocked).
    const stored = getStoredFbCookies();
    if (stored.fbc) return stored.fbc;

    if (typeof window === 'undefined') return undefined;
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');
    if (fbclid) {
        // Meta spec: creationTime is Unix ms when fbclid was first observed.
        const ts = Date.now();
        return `fb.1.${ts}.${fbclid}`;
    }
    return undefined;
}

// ── FB cookie persistence in localStorage ────────────────────────────────────

const FB_COOKIES_STORAGE_KEY = 'mc_fb_cookies';

/**
 * Store _fbp cookie and _fbc (or fbclid-derived) in localStorage.
 * Call on app init so these values survive cookie expiry and are
 * available for server-side events (e.g. upsell purchases later).
 */
export function storeFbCookies(): void {
    if (typeof localStorage === 'undefined') return;
    const fbp = getFbpCookie();
    const fbc = getFbcValue();
    if (!fbp && !fbc) return;
    try {
        const existing = getStoredFbCookies();
        localStorage.setItem(FB_COOKIES_STORAGE_KEY, JSON.stringify({
            fbp: fbp || existing.fbp,
            fbc: fbc || existing.fbc,
        }));
    } catch { /* ignore */ }
}

/**
 * Read stored _fbp/_fbc from localStorage.
 */
export function getStoredFbCookies(): { fbp?: string; fbc?: string } {
    if (typeof localStorage === 'undefined') return {};
    try {
        return JSON.parse(localStorage.getItem(FB_COOKIES_STORAGE_KEY) || '{}');
    } catch {
        return {};
    }
}

/** First-touch Meta cookies for Mongo profile update at lead capture (CAPI match quality). */
export function appendLeadCaptureMetaCookiesToProfile(profileData: Record<string, unknown>): void {
    storeFbCookies();
    const fb = getStoredFbCookies();
    if (fb.fbp) profileData.metaFbp = fb.fbp;
    if (fb.fbc) profileData.metaFbc = fb.fbc;
}

// ── Name splitting ───────────────────────────────────────────────────────────

/**
 * Split a full name string into first name and last name.
 * Handles multi-part last names correctly (e.g. "Maria Jose Garcia" → fn: "Maria", ln: "Jose Garcia").
 * Returns empty ln when only a single name is provided.
 */
export function splitFullName(fullName: string): { fn: string; ln: string } {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 0 || (parts.length === 1 && parts[0] === '')) {
        return { fn: '', ln: '' };
    }
    if (parts.length === 1) {
        return { fn: parts[0], ln: '' };
    }
    const fn = parts[0];
    const ln = parts.slice(1).join(' ');
    return { fn, ln };
}

// ── Meta / GTM helpers ───────────────────────────────────────────────────────

/**
 * Generate a unique event_id for Pixel ↔ CAPI deduplication.
 */
function generateEventId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
}

/**
 * Get or create a persistent external_id for Meta CAPI matching.
 * Stored in localStorage so it remains stable across the session.
 */
function getOrCreateExternalId(): string {
    if (typeof localStorage === 'undefined') return '';
    const key = 'mc_external_id';
    let id = localStorage.getItem(key);
    if (!id) {
        id = `mc_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
        localStorage.setItem(key, id);
    }
    return id;
}

/**
 * Build user_data for Meta events.
 * Values are raw — sGTM/Stape hashes PII server-side.
 * Reads fb cookies from live cookies first, falls back to localStorage.
 */
function buildMetaUserData(overrides?: {
    em?: string;
    fn?: string;
    ln?: string;
}): MetaEventPayload['user_data'] {
    const stored = getStoredFbCookies();
    const fbp = getFbpCookie() || stored.fbp;
    // Prefer persisted first-touch fbc so a lingering ?fbclid= param cannot
    // regenerate creationTime at checkout (Meta invalid-creationTime diagnostic).
    const fbc = stored.fbc || getFbcValue();
    const em = overrides?.em || _userEmail;
    const fn = overrides?.fn || _userFirstName;
    const ln = overrides?.ln || _userLastName;

    return {
        ...(em && { em: em.toLowerCase().trim() }),
        ...(fn && { fn: fn.toLowerCase().trim() }),
        ...(ln && { ln: ln.toLowerCase().trim() }),
        external_id: getOrCreateExternalId(),
        ...(fbp && { fbp }),
        ...(fbc && { fbc }),
        client_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    };
}

/**
 * Push a Meta-specific event to the dataLayer.
 * ONLY 4 event names ever: meta_lead, meta_view_content,
 * meta_initiate_checkout, meta_purchase.
 * GTM triggers filter exclusively on these events for sGTM → CAPI.
 */
function pushMetaEvent(eventName: string, payload: MetaEventPayload) {
    if (typeof window === 'undefined') return;
    ensureDataLayer();
    // Queue GTM's bootstrap before the conversion while keeping this
    // non-blocking; Meta and Axon events replay once scripts finish loading.
    void ensureMarketingTagsReady().catch((error) => {
        console.error('[funnelV3Analytics] Marketing tags failed to load', error);
    });
    window.dataLayer.push({
        event: eventName,
        ...payload,
    });
}

/**
 * Fire-once guard for SPA navigation. Prevents duplicate Meta events
 * when users navigate back/forward in the single-page app.
 * Uses sessionStorage so guards reset on new sessions.
 */
function fbFireOnce(eventKey: string): boolean {
    if (typeof sessionStorage === 'undefined') return true;
    const key = `fb_fired_${eventKey}`;
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    return true;
}

/**
 * Update module-level user state on email capture.
 * Stores email/name so subsequent pushMetaEvent calls include them
 * in user_data. Also persists fb cookies at this high-value moment.
 */
function updateUserState(email: string, firstName?: string) {
    _userEmail = email;
    if (firstName) _userFirstName = firstName;

    // Store fb cookies on email capture (high-value moment)
    storeFbCookies();
}

/**
 * PageView: Amplitude + AppLovin axon_page_view.
 * Meta PageView is handled by the GTM client-side Meta Pixel tag on page load.
 */
export function trackPageView() {
    try {
        ampTrack('page_view', {
            page: typeof window !== 'undefined' ? window.location.pathname : '',
            funnel_version: 'v3',
            ...getActiveExperiments(),
        });

        pushAxonEvent('page_view', {
            user_data: buildAxonUserData(),
        });
    } catch { /* non-blocking */ }
}

// ── Experiment variant enrichment ────────────────────────────────────────────

/**
 * Read active experiment variants from the Amplitude Experiment client.
 * Returns a flat object like { 'exp.funnel-paywall-headline': 'social_proof' }.
 * The Experiment client stores its state on window — we read it without hooks.
 */
function getActiveExperiments(): Record<string, string> {
    const result: Record<string, string> = {};
    try {
        if (typeof window === 'undefined') return result;
        const expClient = (window as any).__AMPLITUDE_EXPERIMENT_CLIENT__;
        if (!expClient) return result;
        const allVariants = expClient.all();
        for (const key of ALL_EXPERIMENT_KEYS) {
            if (allVariants[key]?.value) {
                result[`exp.${key}`] = allVariants[key].value;
            }
        }
    } catch { /* experiment SDK not loaded yet — return empty */ }
    return result;
}

/** Sticky default-plan A/B variant (mt_ab_default_plan), when assigned. */
function getAbVariantProps(): Record<string, string> {
    const abVariant = getAbDefaultPlanVariant();
    return abVariant ? { ab_variant: abVariant } : {};
}

// ── Low-level dispatchers ────────────────────────────────────────────────────

function gtmPush(eventName: string, data?: Record<string, any>) {
    if (typeof window !== 'undefined') {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({ event: eventName, ...data });
    }
}

function ampTrack(eventName: string, data?: Record<string, any>) {
    amplitude.track(eventName, data);
}

function ampSetUserProperties(properties: Record<string, any>) {
    const identify = new amplitude.Identify();
    Object.entries(properties).forEach(([key, value]) => {
        identify.set(key, value);
    });
    amplitude.identify(identify);
}

/**
 * Fire an event to Amplitude + GTM/GA4.
 * Use for funnel milestones that do NOT have a corresponding meta_* event
 * (quiz started, quiz completed, paywall viewed, add payment info, account created).
 * Functions that also push meta_* events use ampTrack() directly instead,
 * so dataLayer gets a single push (the meta_* event) that GTM uses for both
 * the GA4 tag and the Pixel_Event tag.
 * Automatically enriches every event with active experiment variants.
 */
function trackAll(eventName: string, data?: Record<string, any>) {
    const enriched = { ...data, ...getActiveExperiments() };
    ampTrack(eventName, enriched);
    gtmPush(eventName, enriched);
}

// ── Convenience functions ────────────────────────────────────────────────────

export function trackQuizStart(data?: Record<string, any>) {
    const utms = getStoredUTMs();
    const funnelSource = getFunnelSource();
    trackAll(FUNNEL_V3_EVENTS.FUNNEL_STARTED, {
        funnel_version: 'v3',
        funnel_source: funnelSource,
        utm_source: utms.utm_source ?? 'direct',
        utm_medium: utms.utm_medium ?? null,
        utm_campaign: utms.utm_campaign ?? null,
        utm_content: utms.utm_content ?? null,
        utm_term: utms.utm_term ?? null,
        referrer: typeof document !== 'undefined' ? document.referrer || 'direct' : 'direct',
        device_type: typeof navigator !== 'undefined' && /Mobi/.test(navigator.userAgent) ? 'mobile' : 'desktop',
        ...data,
    });
}

// ── No-op functions — kept for component compatibility ──────────────────────
// These micro-events are NOT tracked in Amplitude. Only funnel stages above
// produce analytics events. The function signatures are kept so components
// that call them don't break.

export function trackStepViewed(stepName: string, stepNumber: number) {
    try {
        ampTrack('step_viewed', {
            step: stepName,
            step_index: stepNumber,
            funnel_version: 'v3',
            ...getActiveExperiments(),
        });
    } catch { /* non-blocking */ }
}

export function trackQuizStep(
    stepName: string,
    stepNumber: number,
    answer: string | string[],
    extra?: Record<string, any>
) {
    ampTrack('quiz_answer', {
        step_name: stepName,
        step_number: stepNumber,
        answer,
        funnel_version: 'v3',
        ...extra,
    });
}

export function trackQuizCompleted(personaData: {
    totalQuestionsAnswered: number;
    totalTimeMs: number;
    primaryGoal: string | null;
    painPoints: string[];
    activityLevel: string | null;
    ageRange: string | null;
    walkingHistory: string | null;
    weightPattern: string | null;
    // Enriched quiz answer fields
    currentWeightKg?: number | null;
    goalWeightKg?: number | null;
    heightCm?: number | null;
    birthYear?: number | null;
    stressSymptoms?: string[];
    sleepQuality?: string | null;
    physicalBarriers?: string[];
    targetZones?: string[];
    mainBarrier?: string | null;
    motivation?: string | null;
    rewardPreference?: string | null;
}) {
    const weightToLose = (personaData.currentWeightKg && personaData.goalWeightKg)
        ? personaData.currentWeightKg - personaData.goalWeightKg
        : null;

    trackAll(FUNNEL_V3_EVENTS.QUIZ_COMPLETED, {
        funnel_version: 'v3',
        total_questions_answered: personaData.totalQuestionsAnswered,
        total_time_ms: personaData.totalTimeMs,
        primary_goal: personaData.primaryGoal,
        pain_points: personaData.painPoints,
        activity_level: personaData.activityLevel,
        age_range: personaData.ageRange,
        weight_kg: personaData.currentWeightKg ?? null,
        goal_weight_kg: personaData.goalWeightKg ?? null,
        weight_to_lose_kg: weightToLose,
        height_cm: personaData.heightCm ?? null,
        birth_year: personaData.birthYear ?? null,
        walking_history: personaData.walkingHistory ?? null,
        weight_pattern: personaData.weightPattern ?? null,
        stress_symptoms: personaData.stressSymptoms ?? [],
        sleep_quality: personaData.sleepQuality ?? null,
        physical_barriers: personaData.physicalBarriers ?? [],
        target_zones: personaData.targetZones ?? [],
        main_barrier: personaData.mainBarrier ?? null,
        motivation: personaData.motivation ?? null,
        reward_preference: personaData.rewardPreference ?? null,
    });

    // Set key answers as permanent user properties for segmentation
    ampSetUserProperties({
        primary_goal: personaData.primaryGoal,
        activity_level: personaData.activityLevel,
        age_range: personaData.ageRange,
        walking_history: personaData.walkingHistory,
        weight_pattern: personaData.weightPattern,
        pain_points: personaData.painPoints,
        quiz_completed_at: new Date().toISOString(),
    });
}

export function trackInterstitialViewed(
    interstitialName: string,
    stepNumber: number,
    extra?: Record<string, any>
) {
    try {
        ampTrack('interstitial_viewed', {
            interstitial_id: interstitialName,
            step_index: stepNumber,
            funnel_version: 'v3',
            ...extra,
            ...getActiveExperiments(),
        });
    } catch { /* non-blocking */ }
}

export function trackEmailCaptured(email: string, firstName: string) {
    email = email.toLowerCase().trim();
    const utms = getStoredUTMs();
    const ga4Data = {
        funnel_version: 'v3',
        has_email: true,
        has_name: !!firstName,
        utm_source: utms.utm_source ?? 'direct',
        utm_medium: utms.utm_medium ?? null,
        utm_campaign: utms.utm_campaign ?? null,
        utm_content: utms.utm_content ?? null,
        utm_term: utms.utm_term ?? null,
    };

    // Amplitude only — GA4 fires on meta_lead via GTM
    ampTrack(FUNNEL_V3_EVENTS.LEAD, { ...ga4Data, ...getActiveExperiments() });

    // Update module-level state so user_data is available for subsequent Meta events
    updateUserState(email, firstName);

    // ── Meta: Lead — fire once per session ──
    // GTM triggers both the GA4 tag and Pixel_Event tag on this single push.
    if (fbFireOnce('lead')) {
        const leadEventId = generateEventId('lead');

        pushMetaEvent('meta_lead', {
            ...ga4Data,
            meta_event_name: 'Lead',
            event_id: leadEventId,
            user_data: buildMetaUserData({ em: email, fn: firstName || undefined }),
        });

        pushAxonEvent('generate_lead', {
            currency: 'USD',
            value: AXON_LEAD_VALUE,
            user_data: buildAxonUserData({ em: email }),
        });
    }

    // Amplitude: identify user with MongoDB _id (preferred) or email fallback
    const storedUser = (() => {
        try {
            return JSON.parse(
                localStorage.getItem('user') || '{}'
            );
        } catch { return {}; }
    })();
    const userId = storedUser?.id || email;
    amplitude.setUserId(userId);

    const identifyObj = new amplitude.Identify();
    identifyObj.set('email', email);
    identifyObj.setOnce('first_identified_at', new Date().toISOString());
    amplitude.identify(identifyObj);

    ampSetUserProperties({
        first_name: firstName,
        funnel_version: 'v3',
        quiz_completed: true,
    });
}

export function trackPlanLoadingStarted(extra?: Record<string, any>) {
    try {
        ampTrack('plan_loading_started', {
            funnel_version: 'v3',
            ...extra,
            ...getActiveExperiments(),
        });
    } catch { /* non-blocking */ }
}

export function trackResultsViewed(extra?: Record<string, any>) {
    const ga4Data = { funnel_version: 'v3', ...extra };

    // Amplitude only — GA4 fires on meta_view_content via GTM
    ampTrack(FUNNEL_V3_EVENTS.RESULTS_VIEWED, { ...ga4Data, ...getActiveExperiments() });

    // ── Meta: ViewContent — fire once per session ──
    // GTM triggers both the GA4 tag and Pixel_Event tag on this single push.
    if (fbFireOnce('view_content')) {
        const vcEventId = generateEventId('view_content');

        pushMetaEvent('meta_view_content', {
            ...ga4Data,
            meta_event_name: 'ViewContent',
            event_id: vcEventId,
            content_name: 'quiz_v3_results',
            user_data: buildMetaUserData(),
        });
    }
}

export function trackPaywallViewed(extra?: Record<string, any>) {
    trackAll(FUNNEL_V3_EVENTS.PAYWALL_VIEWED, {
        funnel_version: 'v3',
        ...getAbVariantProps(),
        ...extra,
    });

    if (axonFireOnce('view_item')) {
        pushAxonEvent('view_item', {
            currency: 'USD',
            items: [buildAxonItem('subscription', 0)],
            user_data: buildAxonUserData(),
        });
    }
}

export function trackPaywallView(extra?: Record<string, any>) {
    trackAll(FUNNEL_V3_EVENTS.PAYWALL_VIEW, {
        funnel_version: 'v3',
        ...getAbVariantProps(),
        ...extra,
    });
}

const STRIPE_PROMO_TRACKED_SESSION_PREFIX = 'funnel_v3_stripe_promo_tracked_';

/**
 * Fires when Stripe validates a URL/email promo code successfully (paywall or payment step).
 * Deduped once per browser tab session per promotion code so paywall + payment mounts do not double-count.
 */
export function trackStripePromoValidated(props: {
    promoCode: string;
    discountPercent: number;
    couponDisplayName?: string;
    stripeCouponId?: string;
    surface: 'paywall' | 'payment';
}): void {
    try {
        const normalized = props.promoCode.trim().toUpperCase();
        if (!normalized) return;

        if (typeof sessionStorage !== 'undefined') {
            const dedupeKey = `${STRIPE_PROMO_TRACKED_SESSION_PREFIX}${normalized}`;
            if (sessionStorage.getItem(dedupeKey)) return;
            sessionStorage.setItem(dedupeKey, '1');
        }

        trackAll('stripe_promo_validated', {
            funnel_version: 'v3',
            promo_code: normalized,
            discount_percent: props.discountPercent,
            coupon_display_name: props.couponDisplayName ?? normalized,
            stripe_coupon_id: props.stripeCouponId ?? null,
            surface: props.surface,
        });
    } catch {
        /* non-blocking */
    }
}

const REFERRAL_BANNER_TRACKED_KEY = 'mc_referral_banner_tracked';

function markReferralBannerTracked(outcome: 'shown' | 'invalid'): boolean {
    try {
        if (typeof sessionStorage === 'undefined') return true;
        const previous = sessionStorage.getItem(REFERRAL_BANNER_TRACKED_KEY);
        if (previous) return false;
        sessionStorage.setItem(REFERRAL_BANNER_TRACKED_KEY, outcome);
        return true;
    } catch {
        return true;
    }
}

/** GA4/sGTM: visitor landed with a valid referral code and the invite banner is shown. */
export function trackReferralBannerShown(props?: { referrerFirstName?: string }): void {
    if (!markReferralBannerTracked('shown')) return;
    trackAll('referral_banner_shown', {
        funnel_version: 'v3',
        referrer_first_name: props?.referrerFirstName || undefined,
    });
}

/** GA4/sGTM: landing had a referral code that failed GET /referral/validate. No banner. */
export function trackReferralBannerCodeInvalid(): void {
    if (!markReferralBannerTracked('invalid')) return;
    trackAll('referral_banner_code_invalid', {
        funnel_version: 'v3',
    });
}

export function trackPlanSelected(
    planType: string,
    planId: string,
    price: number,
    extra?: Record<string, any>
) {
    const ga4Data = {
        funnel_version: 'v3',
        plan_type: planType,
        plan_id: planId,
        price,
        currency: 'USD',
        ...getAbVariantProps(),
        ...extra,
    };

    // Amplitude only — GA4 fires on meta_initiate_checkout via GTM
    ampTrack(FUNNEL_V3_EVENTS.BEGIN_CHECKOUT, { ...ga4Data, ...getActiveExperiments() });

    // ── Meta: InitiateCheckout — fire once per session ──
    // GTM triggers both the GA4 tag and Pixel_Event tag on this single push.
    if (fbFireOnce('initiate_checkout')) {
        const icEventId = generateEventId('initiate_checkout');

        pushMetaEvent('meta_initiate_checkout', {
            ...ga4Data,
            meta_event_name: 'InitiateCheckout',
            event_id: icEventId,
            value: price,
            user_data: buildMetaUserData(),
        });

        pushAxonEvent('begin_checkout', {
            currency: 'USD',
            value: price,
            items: [buildAxonItem(planId || planType, price)],
            user_data: buildAxonUserData(),
        });
    }
}

export function trackSelectPlan(planType: string, extra?: Record<string, any>) {
    trackAll(FUNNEL_V3_EVENTS.SELECT_PLAN, {
        funnel_version: 'v3',
        plan_type: planType,
        ...getAbVariantProps(),
        ...extra,
    });
}

export function trackPaymentAttempted(
    planType: string,
    amount: number,
    method: string,
    extra?: Record<string, any>
) {
    // GA4/Amplitude only — no Meta event for AddPaymentInfo
    trackAll(FUNNEL_V3_EVENTS.ADD_PAYMENT_INFO, {
        funnel_version: 'v3',
        plan_type: planType,
        amount,
        currency: 'USD',
        payment_method: method,
        ...getAbVariantProps(),
        ...extra,
    });
}

export function trackPaymentCompleted(purchaseData: {
    planType: string;
    planId?: string;
    amount: number;
    predictedLTV?: number;
    transactionId: string;
    email: string;
    firstName: string;
    lastName?: string;
    fullName?: string;
    tiktokEventId?: string;
    extra?: Record<string, any>;
    quizData?: {
        ageGroup?: string | null;
        primaryGoal?: string | null;
        stressSymptoms?: string[];
        activityLevel?: string | null;
        currentWeightKg?: number | null;
        goalWeightKg?: number | null;
        mainBarrier?: string | null;
        rewardPreference?: string | null;
        walkingHistory?: string | null;
        physicalBarriers?: string[];
    };
}) {
    const { planType, planId, amount, transactionId, email, extra } =
        purchaseData;

    // Split full name into fn/ln when provided; otherwise use firstName/lastName directly
    let fn = purchaseData.firstName;
    let ln = purchaseData.lastName || '';
    if (purchaseData.fullName) {
        const split = splitFullName(purchaseData.fullName);
        fn = split.fn;
        ln = split.ln;
    }

    // Store names for future events (upsell, coaching)
    _userEmail = email;
    if (fn) _userFirstName = fn;
    if (ln) _userLastName = ln;

    // Persist user data in localStorage for upsell/coaching purchases
    if (typeof localStorage !== 'undefined') {
        if (email) localStorage.setItem('mc_user_email', email);
        if (fn) localStorage.setItem('mc_user_fn', fn);
        if (ln) localStorage.setItem('mc_user_ln', ln);
    }

    // Include stored UTMs for revenue → ad attribution
    const utms = getStoredUTMs();

    // Build quiz answer enrichment for the Amplitude purchase event
    const quizEnrichment: Record<string, any> = {};
    if (purchaseData.quizData) {
        const qd = purchaseData.quizData;
        quizEnrichment.age_range = qd.ageGroup ?? null;
        quizEnrichment.primary_goal = qd.primaryGoal ?? null;
        quizEnrichment.pain_points = qd.stressSymptoms ?? [];
        quizEnrichment.activity_level = qd.activityLevel ?? null;
        quizEnrichment.weight_to_lose_kg = (qd.currentWeightKg && qd.goalWeightKg)
            ? qd.currentWeightKg - qd.goalWeightKg
            : null;
        quizEnrichment.main_barrier = qd.mainBarrier ?? null;
        quizEnrichment.reward_preference = qd.rewardPreference ?? null;
        quizEnrichment.walking_history = qd.walkingHistory ?? null;
        quizEnrichment.physical_barriers = qd.physicalBarriers ?? [];
    }

    const ga4Data = {
        funnel_version: 'v3',
        plan_type: planType,
        value: amount,
        currency: 'USD',
        transaction_id: transactionId,
        ...getAbVariantProps(),
        utm_source: utms.utm_source ?? 'direct',
        utm_medium: utms.utm_medium ?? null,
        utm_campaign: utms.utm_campaign ?? null,
        utm_content: utms.utm_content ?? null,
        utm_term: utms.utm_term ?? null,
        // GA4 ecommerce object (picked up by GA4 tags in GTM)
        ecommerce: {
            transaction_id: transactionId,
            value: amount,
            currency: 'USD',
            items: [{ item_name: planType, price: amount, quantity: 1 }],
        },
        ...extra,
    };

    // Amplitude only — GA4 fires on meta_purchase via GTM
    // Quiz enrichment added to Amplitude event only (not GTM/Meta)
    ampTrack(FUNNEL_V3_EVENTS.PURCHASE, { ...ga4Data, ...quizEnrichment, ...getActiveExperiments() });

    // ── Meta: Purchase — NO fire-once guard ──
    // Each purchase is a distinct real event. The Stripe transactionId
    // as event_id guarantees deduplication at Meta's end.
    // GTM triggers both the GA4 tag and Pixel_Event tag on this single push.
    const eventId = transactionId;

    pushMetaEvent('meta_purchase', {
        ...ga4Data,
        meta_event_name: 'Purchase',
        event_id: eventId,
        order_id: transactionId,
        // Override value with predicted LTV for Meta Value Optimization;
        // preserve actual transaction amount in actual_amount for reconciliation.
        value: purchaseData.predictedLTV ?? amount,
        predicted_ltv: purchaseData.predictedLTV,
        actual_amount: amount,
        content_name: planType ? `plan_${planType}` : planId,
        user_data: buildMetaUserData({ em: email, fn: fn || undefined, ln: ln || undefined }),
        // Shared TikTok CAPI dedupe id — browser-side TikTok tag uses this as event_id.
        ...(purchaseData.tiktokEventId && { tiktok_event_id: purchaseData.tiktokEventId }),
        ltv_model: LTV_MODEL,
    });

    pushAxonEvent('purchase', {
        currency: 'USD',
        value: amount,
        transaction_id: transactionId,
        shipping: 0,
        tax: 0,
        items: [buildAxonItem(planId || planType, amount)],
        user_data: buildAxonUserData({ em: email }),
    });

    // Amplitude: set purchase user properties
    ampSetUserProperties({
        paid_user: true,
        plan_type: planType,
        purchase_amount: amount,
        purchase_date: new Date().toISOString(),
    });
}

export function trackPaymentFailed(
    planType: string,
    errorMessage: string,
    paymentMethod?: string,
) {
    ampTrack('payment_failed', {
        plan_type: planType,
        error_message: errorMessage,
        payment_method: paymentMethod || 'card',
        funnel_version: 'v3',
    });
}

export function trackStripeLoadFailed(
    attemptNumber: number,
    totalAttempts: number,
    errorMessage: string,
) {
    const connectionType = (() => {
        if (typeof navigator === 'undefined') return 'unknown';
        const nav = navigator as Navigator & {
            connection?: { effectiveType?: string };
        };
        return nav.connection?.effectiveType ?? 'unknown';
    })();
    ampTrack('stripe_load_failed', {
        attempt_number: attemptNumber,
        total_attempts: totalAttempts,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        connection_type: connectionType,
        error_message: errorMessage,
        funnel_version: 'v3',
    });
}

export function trackStripeRetryClicked() {
    ampTrack('stripe_retry_clicked', {
        funnel_version: 'v3',
    });
}

export function trackAccountCreated(
    params?: { fn?: string; ln?: string },
    extra?: Record<string, any>
) {
    trackAll(FUNNEL_V3_EVENTS.ACCOUNT_CREATED, { funnel_version: 'v3', ...extra });

    // Update module-level names so subsequent events (upsell) have them
    if (params?.fn) _userFirstName = params.fn;
    if (params?.ln) _userLastName = params.ln;

    // ── Meta: CompleteRegistration — fire once per session ──
    if (fbFireOnce('complete_registration')) {
        const crEventId = generateEventId('complete_registration');

        pushMetaEvent('meta_complete_registration', {
            meta_event_name: 'CompleteRegistration' as any,
            event_id: crEventId,
            value: 0,
            currency: 'USD',
            status: true,
            user_data: buildMetaUserData({
                fn: params?.fn || undefined,
                ln: params?.ln || undefined,
            }),
        } as any);
    }
}

// ── Upsell / coaching purchase events ────────────────────────────────────────

/**
 * Track a coaching upsell purchase via meta_purchase dataLayer push.
 * No fire-once guard — each purchase is real and deduped by transactionId.
 */
export function trackCoachingPurchase(amount: number, transactionId: string, predictedLTV?: number) {
    _fireMetaPurchase({
        planId: 'coaching',
        amount,
        transactionId,
        predictedLTV,
    });
}

/**
 * Track a generic upsell purchase via meta_purchase dataLayer push.
 * No fire-once guard — each purchase is real and deduped by transactionId.
 */
export function trackUpsellPurchase(amount: number, planId: string, transactionId: string, predictedLTV?: number) {
    _fireMetaPurchase({
        planId,
        amount,
        transactionId,
        predictedLTV,
    });
}

/**
 * Internal: fire a Purchase event to dataLayer for upsell/coaching.
 * GTM Pixel_Event tag handles the browser-side fbq call.
 * Uses transactionId as event_id directly for deduplication.
 * Retrieves stored user data from the initial purchase.
 */
function _fireMetaPurchase(data: {
    planId: string;
    amount: number;
    transactionId: string;
    predictedLTV?: number;
}) {
    const { planId, amount, transactionId, predictedLTV } = data;
    const eventId = transactionId;

    // Retrieve stored user data from initial purchase
    const em = _userEmail || (typeof localStorage !== 'undefined' ? localStorage.getItem('mc_user_email') || undefined : undefined);
    const fn = _userFirstName || (typeof localStorage !== 'undefined' ? localStorage.getItem('mc_user_fn') || undefined : undefined);
    const ln = _userLastName || (typeof localStorage !== 'undefined' ? localStorage.getItem('mc_user_ln') || undefined : undefined);

    const ga4Data = {
        funnel_version: 'v3',
        plan_type: planId,
        value: amount,
        currency: 'USD',
        transaction_id: transactionId,
        ecommerce: {
            transaction_id: transactionId,
            value: amount,
            currency: 'USD',
            items: [{ item_name: planId, price: amount, quantity: 1 }],
        },
    };

    // Amplitude only — GA4 fires on meta_purchase via GTM
    ampTrack(FUNNEL_V3_EVENTS.PURCHASE, { ...ga4Data, ...getActiveExperiments() });

    // GTM triggers both the GA4 tag and Pixel_Event tag on this single push.
    pushMetaEvent('meta_purchase', {
        ...ga4Data,
        meta_event_name: 'Purchase',
        event_id: eventId,
        order_id: transactionId,
        value: predictedLTV ?? amount,
        predicted_ltv: predictedLTV,
        actual_amount: amount,
        content_name: planId,
        user_data: buildMetaUserData({ em: em || undefined, fn: fn || undefined, ln: ln || undefined }),
        ltv_model: LTV_MODEL,
    });

    pushAxonEvent('purchase', {
        currency: 'USD',
        value: amount,
        transaction_id: transactionId,
        shipping: 0,
        tax: 0,
        items: [buildAxonItem(planId, amount)],
        user_data: buildAxonUserData({ em: em || undefined }),
    });
}

export function trackCoachingUpsellViewed(price: number) {
    try {
        ampTrack('coaching_upsell_viewed', {
            price,
            funnel_version: 'v3',
            ...getActiveExperiments(),
        });
    } catch { /* non-blocking */ }
}

export function trackCoachingUpsellAccepted(_price: number) {
    // No-op — legacy; see trackUpsellAccepted for personalized tracking
}

export function trackCoachingUpsellSkipped() {
    // No-op — legacy; see trackUpsellDeclined for personalized tracking
}

// ── Personalized upsell tracking ────────────────────────────────────────────

interface UpsellTrackingProps {
    headline_variant: string;
    main_barrier: string | null;
    sleep_quality: string | null;
    walking_history: string | null;
    stress_symptoms: string[];
    primary_goal: string | null;
    plan_type: string | null;
}

export function trackUpsellViewed(props: UpsellTrackingProps) {
    amplitude.track('upsell_viewed', props);
}

export function trackUpsellAccepted(props: { headline_variant: string; plan_type: string | null }) {
    amplitude.track('upsell_accepted', props);
}

export function trackUpsellDeclined(props: { headline_variant: string; plan_type: string | null }) {
    amplitude.track('upsell_declined', props);
}

export function trackAppDownload(platform: string, store: string) {
    try {
        ampTrack('app_download', {
            platform,
            store,
            funnel_version: 'v3',
            ...getActiveExperiments(),
        });
    } catch { /* non-blocking */ }
}

export function trackSmsLinkSent() {
    // No-op
}

export function trackPaywallSectionViewed(
    section: 'hero' | 'transformation' | 'science' | 'plans' | 'testimonials' | 'features' | 'faq' | 'guarantee' | 'customer_reviews' | 'plans_bottom'
) {
    try {
        ampTrack('paywall_section_viewed', {
            section,
            funnel_version: 'v3',
            ...getActiveExperiments(),
        });
    } catch { /* non-blocking */ }
}

export function trackFaqExpanded(question: string, index?: number) {
    try {
        ampTrack('faq_expanded', {
            question,
            index,
            funnel_version: 'v3',
            ...getActiveExperiments(),
        });
    } catch { /* non-blocking */ }
}

export function trackTestimonialSwiped(index: number, ageGroup: string) {
    try {
        ampTrack('testimonial_swiped', {
            index,
            age_group: ageGroup,
            funnel_version: 'v3',
            ...getActiveExperiments(),
        });
    } catch { /* non-blocking */ }
}

export function trackPaywallScrollDepth(depth: number) {
    try {
        ampTrack('paywall_scroll_depth', {
            depth,
            funnel_version: 'v3',
            ...getActiveExperiments(),
        });
    } catch { /* non-blocking */ }
}

export function trackPaywallTimeSpent(seconds: number) {
    try {
        ampTrack('paywall_time_spent', {
            seconds,
            funnel_version: 'v3',
            ...getActiveExperiments(),
        });
    } catch { /* non-blocking */ }
}

// ── Exit-intent tracking ────────────────────────────────────────────────────

export function trackExitIntentShown(data: {
    plan: string | null;
    primaryGoal: string | null;
    mainBarrier: string | null;
}) {
    try {
        ampTrack('exit_intent_shown', {
            plan: data.plan,
            primaryGoal: data.primaryGoal,
            mainBarrier: data.mainBarrier,
            funnel_version: 'v3',
            ...getActiveExperiments(),
        });
    } catch { /* non-blocking */ }
}

export function trackExitIntentConverted(data: {
    plan: string | null;
    exitPrice: number;
    originalPrice: number;
}) {
    try {
        ampTrack('exit_intent_converted', {
            plan: data.plan,
            exitPrice: data.exitPrice,
            originalPrice: data.originalPrice,
            funnel_version: 'v3',
            ...getActiveExperiments(),
        });
    } catch { /* non-blocking */ }
}

export function trackExitIntentDismissed(data: { plan: string | null }) {
    try {
        ampTrack('exit_intent_dismissed', {
            plan: data.plan,
            funnel_version: 'v3',
            ...getActiveExperiments(),
        });
    } catch { /* non-blocking */ }
}

// ── Accelerator upsell tracking ─────────────────────────────────────────────

interface AcceleratorTrackingProps {
    headline_variant: string;
    main_barrier: string | null;
    stress_symptoms: string[];
    target_zones: string[];
    primary_goal: string | null;
    plan_type: string | null;
    coaching_upsell_accepted: boolean;
}

export function trackAcceleratorUpsellViewed(props: AcceleratorTrackingProps) {
    amplitude.track('accelerator_upsell_viewed', props);
}

export function trackAcceleratorUpsellAccepted(props: {
    headline_variant: string;
    plan_type: string | null;
    amount: number;
    currency: string;
    coaching_upsell_accepted: boolean;
}) {
    amplitude.track('accelerator_upsell_accepted', props);
}

export function trackAcceleratorUpsellDeclined(props: {
    headline_variant: string;
    plan_type: string | null;
    coaching_upsell_accepted: boolean;
}) {
    amplitude.track('accelerator_upsell_declined', props);
}
